import { typography, radius } from '@/shared/theme/tokens';
import { BOARD } from './boardTheme';
import { SplitFlap } from './SplitFlap';

const MONO = BOARD.mono;

const LABEL = {
  fontFamily: MONO, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
  letterSpacing: typography.letterSpacing.wider, textTransform: 'uppercase',
};

/**
 * The board's giant signage banner. The single most important real estate: one
 * unambiguous, room-readable state driven entirely by `headline` (its precedence
 * + colour + label are computed in the shared model). A two-tone status lamp,
 * the GIANT state word, and the count rendered as split-flap numerals. Above it,
 * a thin destinations-board chrome strip: plant identity, signal, freshness, clock.
 */
export function BoardBanner({ headline, deploymentName, clock, freshness, jitterActive, animate }) {
  const pulses = headline.state === 'critical' || headline.state === 'degraded';
  const subline = headline.state === 'degraded'
    ? ['STALE DATA · ADVISORY HELD', headline.subtext].filter(Boolean).join('     ·     ')
    : headline.subtext;
  const time = clock.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const date = clock.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' }).toUpperCase();
  const signalColor = jitterActive ? BOARD.warn : BOARD.ok;

  return (
    <div style={{ flexShrink: 0, background: BOARD.board }}>
      {/* ── chrome strip: identity · signal · freshness · clock ───────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14, height: 34, padding: '0 16px',
        background: BOARD.chrome, borderBottom: `1px solid ${BOARD.seam}`, color: BOARD.inkDim, ...LABEL,
        overflow: 'hidden',
      }}>
        {/* furnace identity glyph (supporting anchor — NOT the hero) */}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: BOARD.amber, flexShrink: 0 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>factory</span>
          <span style={{ color: BOARD.ink, letterSpacing: typography.letterSpacing.wide, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>
            {deploymentName || 'CONTROL ROOM'}
          </span>
        </span>
        <Sep />
        <span style={{ flexShrink: 0, color: BOARD.inkFaint }}>FURNACE LINE</span>

        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: signalColor }}>
            <Lamp color={signalColor} animate={animate} />
            {jitterActive ? 'DEMO' : 'LIVE'}
          </span>
          <Sep />
          <Freshness freshness={freshness} animate={animate} />
          <Sep />
          <span style={{ color: BOARD.ink, fontVariantNumeric: 'tabular-nums', letterSpacing: typography.letterSpacing.wide }}>{time}</span>
          <span style={{ color: BOARD.inkFaint }}>{date}</span>
        </span>
      </div>

      {/* ── giant signage row ─────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'stretch', minHeight: 132,
        background: headline.bgDark, borderBottom: `2px solid ${headline.border}`,
      }}>
        {/* status lamp block (contained, not a side-stripe) */}
        <div style={{
          flexShrink: 0, width: 132, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: headline.color, color: BOARD.wall,
          animation: animate && pulses ? 'board-pulse 1.25s steps(1,end) infinite' : 'none',
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 72 }}>{headline.icon}</span>
        </div>

        {/* state word (scaled up for room-scale signage) + count flaps */}
        <div style={{
          flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center',
          gap: 12, padding: '0 24px', overflow: 'hidden',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
            <span style={{
              fontFamily: MONO, fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold,
              letterSpacing: typography.letterSpacing.wider, color: headline.color,
              border: `1px solid ${headline.border}`, borderRadius: radius.xs, padding: '4px 10px', flexShrink: 0,
            }}>
              {headline.code}
            </span>
            {/* clip box (unscaled) decoupled from the scaled glyphs so the giant
                state word can never overflow into the count flaps */}
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
              <span style={{
                display: 'inline-block', transform: 'scale(1.42)', transformOrigin: 'left center',
                fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize['7xl'],
                fontWeight: typography.fontWeight.extrabold, letterSpacing: typography.letterSpacing.wide,
                textTransform: 'uppercase', color: headline.color, lineHeight: 1, whiteSpace: 'nowrap',
              }}>
                {headline.label}
              </span>
            </span>
            {headline.count > 0 && (
              <span style={{ flexShrink: 0 }}>
                <SplitFlap value={headline.count} color={headline.color} size="lg" animate={animate} />
              </span>
            )}
          </div>
          {subline && (
            <span style={{
              fontFamily: MONO, fontSize: typography.fontSize.sm, letterSpacing: typography.letterSpacing.wide,
              color: BOARD.inkFaint, fontVariantNumeric: 'tabular-nums',
            }}>
              {subline}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function Freshness({ freshness, animate }) {
  const { stale, ageSeconds } = freshness;
  const color = stale ? BOARD.warn : BOARD.ok;
  const ageLabel = ageSeconds < 2 ? 'NOW' : `${ageSeconds}s`;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color }}>
      <Lamp color={color} animate={animate && !stale} />
      <span>{stale ? 'STALE' : 'DATA'}</span>
      <span style={{ color: BOARD.inkFaint, fontVariantNumeric: 'tabular-nums' }}>{ageLabel}</span>
    </span>
  );
}

function Lamp({ color, animate }) {
  return (
    <span style={{
      width: 8, height: 8, borderRadius: radius.full, background: color, flexShrink: 0,
      boxShadow: `0 0 7px ${color}`,
      animation: animate ? 'board-pulse 1.8s steps(1,end) infinite' : 'none',
    }} />
  );
}

function Sep() {
  return <span style={{ color: BOARD.seam }}>│</span>;
}
